import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './AiTrades.css';

export default function AiTrackRecord() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState('');

  // filters
  const [hitAllow, setHitAllow] = useState(() => new Set(['tp', 'sl', 'none'])); // include by default
  const [accAllow, setAccAllow] = useState(() => new Set(['high', 'medium', 'low', 'invalid', 'unknown']));
  const [expDirSel, setExpDirSel] = useState(() => new Set());   // expectedDirection24h filter (multi)
  const [trendSel, setTrendSel] = useState(() => new Set());     // trendPhase filter (multi)
  const [pulseSel, setPulseSel] = useState(() => new Set());     // marketPulse filter (multi)
  const [mcMin, setMcMin] = useState('');
  const [mcMax, setMcMax] = useState('');

  // date filters
  const [startDate, setStartDate] = useState(() => localStorage.getItem('aiPerfStart') || '');
  const [endDate, setEndDate] = useState(() => localStorage.getItem('aiPerfEnd') || '');
  useEffect(() => { localStorage.setItem('aiPerfStart', startDate); }, [startDate]);
  useEffect(() => { localStorage.setItem('aiPerfEnd', endDate); }, [endDate]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'token', direction: 'asc' });

  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5000' : 'https://www.trendscout.club';

  // small helper: allow both possible endpoints; use whichever responds first
  async function fetchTrackRecords() {
    const endpoints = [
      `${baseUrl}/api/ai-track-record/list`,
      `${baseUrl}/api/ai-history/list`, // fallback if the first doesn’t exist
    ];
    let lastErr = null;
    for (const url of endpoints) {
      try {
        const { data } = await axios.get(url, { timeout: 15000 });
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.items)) return data.items;
      } catch (e) {
        lastErr = e;
        // continue trying next endpoint
      }
    }
    throw lastErr || new Error('No track-record endpoint reachable');
  }

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchTrackRecords()
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
        // quick debug
        console.log('[AiTrackRecord] fetched items:', (data || []).length);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[AiTrackRecord] fetch failed:', e);
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [baseUrl]);

  // helpers
  const inDateRange = (ts) => {
    if (!startDate && !endDate) return true;          // no date filters → include
    if (!ts) return true;                             // missing timestamp → include
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) return true;                 // invalid timestamp → include

    const s = startDate ? new Date(`${startDate}T00:00:00`).getTime() : -Infinity;
    const e = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : Infinity;
    return t >= s && t <= e;
  };

  const normAcc = (s) => String(s || 'unknown').toLowerCase(); // high/medium/low/invalid/unknown
  const toNum = (v) => {
    if (v === '' || v == null) return null;           // <- critical: empty means "no filter"
    const s = typeof v === 'string' ? v.replace(/,/g, '') : v;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };


  // collect unique option sets for filters (from current data)
  const optionSets = useMemo(() => {
    const expDirs = new Set();
    const trends = new Set();
    const pulses = new Set();
    for (const row of (items || [])) {
      for (const e of (row?.entries || [])) {
        if (e?.expectedDirection24h) expDirs.add(e.expectedDirection24h);
        if (e?.trendPhase) trends.add(e.trendPhase);
        if (e?.marketPulse) pulses.add(e.marketPulse);
      }
    }
    return {
      expectedDirections: Array.from(expDirs).sort(),
      trends: Array.from(trends).sort(),
      pulses: Array.from(pulses).sort(),
    };
  }, [items]);

  // flattened rows with all filters applied
  const flat = useMemo(() => {
    const minMC = toNum(mcMin);
    const maxMC = toNum(mcMax);

    const rows = [];
    for (const h of (items || [])) {
      const token = h?.token ?? '';
      for (const e of (h?.entries || [])) {
        if (!e) continue;

        if (!inDateRange(e.timestamp)) continue;
        if (q && !String(token).toLowerCase().includes(q.toLowerCase())) continue;

        // targetHit filter
        const hit = e.targetHit || 'none';
        if (!hitAllow.has(hit)) continue;

        // accuracy filter
        const acc = normAcc(e.aiAccuracyRating);
        if (!accAllow.has(acc)) continue;

        // context filters (multi-select)
        if (expDirSel.size && e.expectedDirection24h && !expDirSel.has(e.expectedDirection24h)) continue;
        if (trendSel.size && e.trendPhase && !trendSel.has(e.trendPhase)) continue;
        if (pulseSel.size && e.marketPulse && !pulseSel.has(e.marketPulse)) continue;

        // marketCap range filter
        const mc = toNum(e.marketCap);
        if (minMC != null && mc != null && mc < minMC) continue;
        if (maxMC != null && mc != null && mc > maxMC) continue;

        rows.push({ token, agg: h, ...e });
      }
    }
    return rows;
  }, [items, q, hitAllow, accAllow, expDirSel, trendSel, pulseSel, mcMin, mcMax, startDate, endDate]);

  // headline stats on filtered rows
  const stats = useMemo(() => {
    const total = flat.length;
    const tp = flat.filter(e => e.targetHit === 'tp').length;
    const sl = flat.filter(e => e.targetHit === 'sl').length;
    const neither = flat.filter(e => (e.targetHit || 'none') === 'none').length;

    const sumPL = flat.reduce((s, e) => s + (typeof e.profitLoss === 'number' ? e.profitLoss : 0), 0);
    const sumHrs = flat
      .filter(e => e.targetHit === 'tp' || e.targetHit === 'sl')
      .reduce((s, e) => s + (Number(e.daysToOutcome) || 0), 0);

    const hitCount = flat.filter(e => e.targetHit === 'tp' || e.targetHit === 'sl').length;
    const avgPL = total ? (sumPL / total) : 0;
    const avgHrs = hitCount ? (sumHrs / hitCount) : 0;

    const accRatings = flat.reduce((acc, e) => {
      const r = e?.aiAccuracyRating || 'Unknown';
      acc[r] = (acc[r] || 0) + 1;
      return acc;
    }, {});

    const correct = flat.filter(e => normAcc(e.aiAccuracyRating) === 'high').length;
    const incorrect = total - correct;

    return { total, tp, sl, neither, avgPL, avgHrs, accRatings, correct, incorrect };
  }, [flat]);

  // group by token AFTER filters to compute per-token aggregates
  const perToken = useMemo(() => {
    const map = new Map();
    for (const e of flat) {
      if (!map.has(e.token)) map.set(e.token, []);
      map.get(e.token).push(e);
    }
    const rows = [];
    for (const [token, entries] of map) {
      const trades = entries.length;
      const tpCount = entries.filter(x => x.targetHit === 'tp').length;
      const slCount = entries.filter(x => x.targetHit === 'sl').length;
      const totalProfitLoss = entries.reduce((s, x) => s + (Number(x.profitLoss) || 0), 0);

      // avg time only for TP/SL
      const hitEntries = entries.filter(x => x.targetHit === 'tp' || x.targetHit === 'sl');
      const avgTimeHrs = hitEntries.length
        ? hitEntries.reduce((s, x) => s + (Number(x.daysToOutcome) || 0), 0) / hitEntries.length
        : 0;

      const denom = tpCount + slCount;
      const successRatio = denom ? (tpCount / denom) * 100 : 0;

      rows.push({ token, trades, tpCount, slCount, totalProfitLoss, avgTimeHrs, successRatio, entries });
    }
    return rows;
  }, [flat]);

  // sorting on token rows
  const sortedTokens = useMemo(() => {
    const arr = [...perToken];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (key === 'token') return dir * a.token.localeCompare(b.token);
      if (key === 'trades') return dir * (a.trades - b.trades);
      if (key === 'tpCount') return dir * (a.tpCount - b.tpCount);
      if (key === 'slCount') return dir * (a.slCount - b.slCount);
      if (key === 'totalProfitLoss') return dir * (a.totalProfitLoss - b.totalProfitLoss);
      if (key === 'avgTimeHrs') return dir * (a.avgTimeHrs - b.avgTimeHrs);
      if (key === 'successRatio') return dir * (a.successRatio - b.successRatio);
      return 0;
    });
    return arr;
  }, [perToken, sortConfig]);

  const toggleSort = (key) => {
    setSortConfig(prev => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }
    ));
  };

  // toggle helpers for multi-selects
  const toggleInSet = (setObj, setter, value) => {
    const next = new Set(setObj);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const renderMultiChips = (label, options, setObj, setter) => (
    <div className="expanded-section-tradehist">
      <h4>{label}</h4>
       <div className="filter-bar-tradehist">
        {options.map(opt => (
          <button
            key={opt}
            className={setObj.has(opt) ? 'active-db' : ''}
            onClick={() => toggleInSet(setObj, setter, opt)}
          >
            {opt}
          </button>
        ))}
        <button onClick={() => setter(new Set())}>Clear</button>
        <button onClick={() => setter(new Set(options))}>All</button>
      </div>
    </div>
  );

  // ---- STATES: loading / error / empty ----
  if (isLoading) {
    return (
      <div className="loading-modal" role="alert" aria-busy="true" aria-live="polite">
        <div className="loading-content">
          <div className="loader" />
          <p>Loading AI performance…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-box">
        <strong>Could not load performance data.</strong>
        <div style={{ marginTop: 6 }}>{String(error?.message || error)}</div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="outcome-table-wrapper-dashboard">
        <div className="outcome-table">
          <h2>📊 AI Performance (Mongo)</h2>
          <div className="expanded-content">
            <div className="expanded-section">
              <p>No track records found. Make sure your backend exposes either:</p>
              <ul style={{ marginTop: 6 }}>
                <li><code>/api/ai-track-record/list</code></li>
                <li><code>/api/ai-history/list</code></li>
              </ul>
              <p style={{ marginTop: 6, opacity: 0.8 }}>
                If you just ran the migration, verify Mongo has <code>AiTrackRecord</code> documents.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- MAIN UI ----
  return (
    <div className="outcome-table-wrapper-dashboard">
      <div className="outcome-table">
        <h2>📊 AI Performance (Mongo)</h2>

        {/* Headline blocks */}
        <div className="expanded-content">
          <div className="expanded-section">
            <h4>📈 Performance Summary (filtered)</h4>
            <p><strong>Total Trades:</strong> {stats.total}</p>
            <p><strong>TP Hits:</strong> {stats.tp}</p>
            <p><strong>SL Hits:</strong> {stats.sl}</p>
            <p><strong>Neither Hit:</strong> {stats.neither}</p>
            <p><strong>Avg Time to TP/SL:</strong> {stats.avgHrs.toFixed(2)} Hrs</p>
          </div>

          <div className="expanded-section">
            <h4>💡 Averages (filtered)</h4>
            <p><strong>Profit/Loss:</strong> {stats.avgPL.toFixed(2)}%</p>
            {Object.entries(stats.accRatings).map(([rating, count]) => (
              <p key={rating}><strong>{rating}:</strong> {count}</p>
            ))}
            <div className="aidashbuttoncontainer">
              <button
                onClick={() => {
                  if (!flat.length) return;
                  const headers = [
                    'token','timestamp','marketPulse','recommendation','score','marketCap',
                    'aiLabel','trendPhase','expectedDirection24h',
                    'targetHit','daysToOutcome','profitLoss','humanLabel','aiAccuracyRating'
                  ];
                  const csv = [
                    headers.join(','),
                    ...flat.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')),
                  ].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'ai-performance.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                ⬇️ Export CSV
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="expanded-section">
            <h4>🔎 Filters</h4>
            <div className="filter-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder="Search token…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ minWidth: 220 }}
              />
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              <button onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
            </div>
          </div>

          {/* MarketCap range */}
          <div className="expanded-section">
            <h4>💰 MarketCap Range</h4>
            <div className="filter-bar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label>Min</label>
              <input type="number" value={mcMin} onChange={(e) => setMcMin(e.target.value)} placeholder="e.g., 10000000" />
              <label>Max</label>
              <input type="number" value={mcMax} onChange={(e) => setMcMax(e.target.value)} placeholder="e.g., 5000000000" />
              <button onClick={() => { setMcMin(''); setMcMax(''); }}>Clear</button>
            </div>
          </div>
          {/* TargetHit include/exclude */}
          <div className="expanded-section-tradehist">
            <h4>🎯 Include targetHit</h4>
            <div className="filter-bar-tradehist">
              {['tp','sl','none'].map(h => (
                <label key={h} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={hitAllow.has(h)}
                    onChange={() => toggleInSet(hitAllow, setHitAllow, h)}
                  />
                  {h.toUpperCase()}
                </label>
              ))}
               </div>
              <div className="filter-bar-tradehist">
              <button style={{ width: "100px" }} onClick={() => setHitAllow(new Set(['tp','sl','none']))}>All</button>
              <button style={{ width: "100px" }} onClick={() => setHitAllow(new Set())}>None</button>
              </div>
          </div>

          {/* Accuracy include/exclude */}
          <div className="expanded-section-tradehist">
            <h4>🧠 Include Accuracy</h4>
             <div className="filter-bar-tradehist">
              {['high','medium','low','invalid','unknown'].map(h => (
                <label key={h} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={accAllow.has(h)}
                    onChange={() => toggleInSet(accAllow, setAccAllow, h)}
                  />
                  {h[0].toUpperCase() + h.slice(1)}
                </label>
              ))}
              </div>
              <div className="filter-bar-tradehist">
              <button style={{ width: "100px" }} onClick={() => setAccAllow(new Set(['high','medium','low','invalid','unknown']))}>All</button>
              <button style={{ width: "100px" }} onClick={() => setAccAllow(new Set())}>None</button>
            </div>
          </div>

          {/* Context filters (multi-select chips) */}
          {renderMultiChips('Expected Direction 24h', optionSets.expectedDirections, expDirSel, setExpDirSel)}
          {renderMultiChips('Trend Phase', optionSets.trends, trendSel, setTrendSel)}
          {renderMultiChips('Market Pulse', optionSets.pulses, pulseSel, setPulseSel)}
        </div>

        {/* Token table */}
        <div className="table-scroll-container" style={{marginTop: 10}}>
          <table>
            <thead>
              <tr>
                <th onClick={() => toggleSort('token')}>Token</th>
                <th onClick={() => toggleSort('trades')}>Trades</th>
                <th onClick={() => toggleSort('tpCount')}>TP</th>
                <th onClick={() => toggleSort('slCount')}>SL</th>
                <th onClick={() => toggleSort('successRatio')}>Success %</th>
                <th onClick={() => toggleSort('avgTimeHrs')}>Avg Hrs (TP/SL)</th>
                <th onClick={() => toggleSort('totalProfitLoss')}>Total P/L (%)</th>
                <th>Expand</th>
              </tr>
            </thead>
            <tbody>
              {sortedTokens.map(h => (
                <React.Fragment key={h.token}>
                  <tr>
                    <td>{h.token}</td>
                    <td>{h.trades}</td>
                    <td>{h.tpCount}</td>
                    <td>{h.slCount}</td>
                    <td>{h.successRatio.toFixed(1)}%</td>
                    <td>{h.avgTimeHrs.toFixed(2)}</td>
                    <td>{(h.totalProfitLoss ?? 0).toFixed(2)}</td>
                    <td>
                      <button onClick={() => setExpanded(p => ({ ...p, [h.token]: !p[h.token] }))}>
                        {expanded[h.token] ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>
                  {expanded[h.token] && (
                    <tr className="expanded-row">
                      <td colSpan={8}>
                        <div className="expanded-content">
                          <div className="expanded-section">
                            <h4>📜 Entries</h4>
                            <div className="table-scroll-container" style={{ maxHeight: 380 }}>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Rec.</th>
                                    <th>Score</th>
                                    <th>Pulse</th>
                                    <th>AI Label</th>
                                    <th>Trend Phase</th>
                                    <th>Exp 24h</th>
                                    <th>Hit</th>
                                    <th>P/L (%)</th>
                                    <th>Hrs</th>
                                    <th>Human</th>
                                    <th>Accuracy</th>
                                    <th>MC</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {h.entries.slice().reverse().map((e, idx) => (
                                    <tr key={idx}>
                                      <td>{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</td>
                                      <td>{e.recommendation ?? ''}</td>
                                      <td>{e.score ?? ''}</td>
                                      <td>{e.marketPulse ?? ''}</td>
                                      <td>{e.aiLabel ?? ''}</td>
                                      <td>{e.trendPhase ?? ''}</td>
                                      <td>{e.expectedDirection24h ?? ''}</td>
                                      <td>{e.targetHit ?? ''}</td>
                                      <td>{typeof e.profitLoss === 'number' ? e.profitLoss.toFixed(2) : ''}</td>
                                      <td>{e.daysToOutcome ?? ''}</td>
                                      <td>{e.humanLabel ?? ''}</td>
                                      <td>{e.aiAccuracyRating ?? ''}</td>
                                      <td>{e.marketCap != null ? Number(e.marketCap).toLocaleString() : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
